/**
 * ReportDateRangePicker — faithful port of the custom "DropdownRefined" (R5) date picker,
 * shared across the reports (Funded, Pipeline Status, Company Revenue). A hairline trigger
 * pill opens a 772px popover with a preset rail
 * (left) and a two-month range calendar (right). Presets apply instantly; a custom range
 * auto-applies on the second click; the first click always starts a fresh range.
 *
 * Styling is ported faithfully from the mock (inline styles + the original hex palette),
 * kept as scoped local constants so it never touches the app's shadcn tokens. Light mode only.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PRESETS, makePreset, makeCustom, fmtDate, fmtRange, type ReportPeriod } from './reportPeriod';

// ── Per-component palettes (kept separate for pixel fidelity — do not unify) ──
const GRID = {
  ink: '#111827',
  faint: '#9aa3b2',
  line: '#e8eaf0',
  blue: '#2563eb',
  blueSoft: '#f3f6fb',
  blueBand: '#dde7fb',
  rangeText: '#2b4a8f',
  disabled: '#c7cdd6'
};
const CAL = {
  ink: '#0f172a',
  muted: '#64748b',
  faint: '#94a3b8',
  line: '#e2e8f0',
  surface: '#f8fafc',
  blue: '#2563eb'
};
const TRIG = {
  ink: '#111827',
  muted: '#5b6573',
  faint: '#9aa3b2',
  line: '#e8eaf0',
  lineSoft: '#f1f2f6',
  surface: '#f7f8fa',
  divider: '#e6e8ee',
  blue: '#2563eb',
  blueSoft: '#eef3fc'
};

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MON_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface DraftRange {
  start: Date | null;
  end: Date | null;
}

const startOfDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// ── Single month grid ──
interface MonthGridProps {
  year: number;
  month: number;
  sel: DraftRange;
  hover: Date | null;
  onPick: (d: Date) => void;
  onHover: (d: Date | null) => void;
  max?: Date;
}

function MonthGrid({ year, month, sel, hover, onPick, onHover, max }: MonthGridProps) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startDow; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= days; d++) {
    cells.push(new Date(year, month, d));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const a = sel.start ? startOfDayLocal(sel.start) : null;
  let b = sel.end ? startOfDayLocal(sel.end) : null;
  if (a && !b && hover) {
    b = startOfDayLocal(hover);
  }
  const lo = a && b ? (a < b ? a : b) : a;
  const hi = a && b ? (a < b ? b : a) : a;
  const today = new Date();

  return (
    <div style={{ width: 244 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 6 }}>
        {DOW.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 600, color: GRID.faint, letterSpacing: 0.3, padding: '2px 0' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', rowGap: 3 }}>
        {cells.map((d, i) => {
          if (!d) {
            return <div key={i} />;
          }
          const ds = startOfDayLocal(d);
          const disabled = Boolean(max && ds > startOfDayLocal(max));
          const isStart = Boolean(lo && ds.getTime() === lo.getTime());
          const isEnd = Boolean(hi && ds.getTime() === hi.getTime());
          const isEnds = isStart || isEnd;
          const within = Boolean(lo && hi && ds > lo && ds < hi);
          const isToday = isSameDay(d, today);
          const single = Boolean(lo && hi && lo.getTime() === hi.getTime());
          return (
            <div key={i} style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {(within || (lo && hi && !single && isEnds)) && (
                <div style={{
                  position: 'absolute', top: 3, bottom: 3, background: GRID.blueBand,
                  left: isStart && !isEnd ? '50%' : 0, right: isEnd && !isStart ? '50%' : 0,
                  borderTopLeftRadius: isStart ? 9 : 0, borderBottomLeftRadius: isStart ? 9 : 0,
                  borderTopRightRadius: isEnd ? 9 : 0, borderBottomRightRadius: isEnd ? 9 : 0
                }} />
              )}
              <button
                type="button"
                disabled={disabled}
                aria-label={format(d, 'MMM dd, yyyy')}
                onMouseEnter={() => {
                  if (!disabled) {
                    onHover(d);
                  }
                }}
                onClick={() => {
                  if (!disabled) {
                    onPick(d);
                  }
                }}
                style={{
                  position: 'relative', width: 32, height: 32, border: 'none', borderRadius: 9,
                  cursor: disabled ? 'default' : 'pointer', fontSize: 13,
                  fontWeight: isEnds || isToday ? 600 : 450,
                  fontFamily: 'inherit',
                  color: disabled ? GRID.disabled : isEnds ? '#fff' : within ? GRID.rangeText : GRID.ink,
                  background: isEnds ? GRID.blue : 'transparent',
                  boxShadow: isEnds ? '0 1px 4px rgba(37,99,235,.38)' : 'none',
                  transition: 'background .12s, color .12s'
                }}
                onMouseOver={(e) => {
                  if (!disabled && !isEnds && !within) {
                    e.currentTarget.style.background = GRID.blueSoft;
                  }
                }}
                onMouseOut={(e) => {
                  if (!isEnds && !within) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >{d.getDate()}
                {isToday && !isEnds && (
                  <span style={{ position: 'absolute', bottom: 4.5, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: 2, background: GRID.blue }} />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Calendar header pieces — hoisted to module scope so month-nav buttons keep a
//    stable component identity (defining them inline would remount + drop focus on click) ──
function CalNav({ dir, onClick }: { dir: 'l' | 'r'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={dir === 'l' ? 'Previous month' : 'Next month'}
      onClick={onClick}
      style={{ width: 26, height: 26, border: 'none', background: 'transparent', borderRadius: 7, cursor: 'pointer', color: CAL.faint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = CAL.surface;
        e.currentTarget.style.color = CAL.muted;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = CAL.faint;
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d={dir === 'l' ? 'M9 3L5 7l4 4' : 'M5 3l4 4-4 4'} /></svg>
    </button>
  );
}

function CalTitle({ d }: { d: Date }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 700, color: CAL.ink }}>{MON_FULL[d.getMonth()]} {d.getFullYear()}</div>
  );
}

// ── Dual-month range calendar (start fresh, auto-apply on the second click) ──
interface CompactCalendarProps {
  value: ReportPeriod;
  onApply: (p: ReportPeriod) => void;
  max?: Date;
}

function CompactCalendar({ value, onApply, max }: CompactCalendarProps) {
  const init = value.from ? value.from : new Date();
  const [view, setView] = useState(new Date(init.getFullYear(), init.getMonth(), 1));
  const [draft, setDraft] = useState<DraftRange>({ start: value.from ?? null, end: value.to ?? null });
  const [hover, setHover] = useState<Date | null>(null);
  const right = new Date(view.getFullYear(), view.getMonth() + 1, 1);

  const pick = (d: Date) => {
    if (!draft.start || (draft.start && draft.end)) {
      setDraft({ start: d, end: null });
    } else {
      const r: DraftRange = d < draft.start ? { start: d, end: draft.start } : { start: draft.start, end: d };
      setDraft(r);
      if (r.start && r.end) {
        onApply(makeCustom(r.start, r.end));
      }
    }
  };

  const shiftView = (months: number) => {
    setView(new Date(view.getFullYear(), view.getMonth() + months, 1));
  };

  const grid = (d: Date) => (
    <MonthGrid year={d.getFullYear()} month={d.getMonth()} sel={draft} hover={hover} onPick={pick} onHover={setHover} max={max} />
  );

  return (
    <div style={{ width: 544 }} onMouseLeave={() => setHover(null)}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <CalNav dir="l" onClick={() => shiftView(-1)} />
        <CalTitle d={view} />
        <CalTitle d={right} />
        <CalNav dir="r" onClick={() => shiftView(1)} />
      </div>
      <div style={{ display: 'flex', gap: 28 }}>{grid(view)}{grid(right)}</div>
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${CAL.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: CAL.muted, fontVariantNumeric: 'tabular-nums' }}>
          {draft.start ? fmtDate(draft.start) : 'Pick start'} <span style={{ color: CAL.faint }}>→</span> {draft.end ? fmtDate(draft.end) : <span style={{ color: CAL.blue }}>pick end</span>}
        </div>
      </div>
    </div>
  );
}

// ── Trigger + popover (the exported picker) ──
interface ReportDateRangePickerProps {
  value: ReportPeriod;
  onChange: (p: ReportPeriod) => void;
  max?: Date;
  className?: string;
}

export function ReportDateRangePicker({ value, onChange, max, className }: ReportDateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [calendarSession, setCalendarSession] = useState(0);
  const cap = max ?? new Date();
  const isCustom = value.mode === 'custom';

  return (
    <Popover open={open} onOpenChange={(nextOpen) => {
      if (nextOpen) {
        setCalendarSession((session) => session + 1);
      }
      setOpen(nextOpen);
    }}>
      <PopoverTrigger asChild>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '7px 11px', border: `1px solid ${open ? TRIG.blue : TRIG.line}`, background: '#fff', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', boxShadow: open ? `0 0 0 3px ${TRIG.blueSoft}` : '0 1px 2px rgba(16,24,40,.045)', transition: 'border-color .15s, box-shadow .15s' }}
        onMouseOver={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = '#d4d8e0';
          }
        }}
        onMouseOut={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = TRIG.line;
          }
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={open ? TRIG.blue : TRIG.faint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="16" rx="3" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: TRIG.ink, letterSpacing: -0.1 }}>{value.label}</span>
        <span style={{ width: 1, height: 13, background: TRIG.divider }} />
        <span style={{ fontSize: 13, fontWeight: 450, color: TRIG.muted, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.1 }}>{fmtRange(value)}</span>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={TRIG.faint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2, transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}><path d="M2.5 4.5L6 8l3.5-3.5" /></svg>
      </button>
      </PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-label="Select reporting period"
        align="end"
        sideOffset={7}
        collisionPadding={8}
        className="flex overflow-hidden p-0"
        style={{ width: 772, maxWidth: 'calc(100vw - 16px)', background: '#fff', border: `1px solid ${TRIG.line}`, borderRadius: 12, boxShadow: '0 16px 40px -12px rgba(16,24,40,.18), 0 4px 12px -6px rgba(16,24,40,.08)' }}
      >
          <div style={{ width: 186, flexShrink: 0, borderRight: `1px solid ${TRIG.lineSoft}`, padding: 8, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            {PRESETS.map((q) => {
              const on = value.mode === q.id;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => {
                    onChange(makePreset(q.id, cap));
                    setOpen(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '8px 11px', border: 'none', background: on ? TRIG.blueSoft : 'transparent', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: on ? 600 : 500, color: on ? TRIG.blue : TRIG.muted, marginBottom: 1, whiteSpace: 'nowrap', transition: 'background .12s, color .12s' }}
                  onMouseOver={(e) => {
                    if (!on) {
                      e.currentTarget.style.background = TRIG.lineSoft;
                      e.currentTarget.style.color = TRIG.ink;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!on) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = TRIG.muted;
                    }
                  }}
                >
                  {q.label}
                  {on && <svg width="12" height="12" viewBox="0 0 13 13" fill="none" stroke={TRIG.blue} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7l3 3 5-6" /></svg>}
                </button>
              );
            })}
            <div style={{ flex: 1, minHeight: 8 }} />
            <div style={{ borderTop: `1px solid ${TRIG.lineSoft}`, margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 11px 4px', fontSize: 11.5, fontWeight: 500, color: isCustom ? TRIG.blue : TRIG.faint, whiteSpace: 'nowrap' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><rect x="3.5" y="5" width="17" height="16" rx="3" /><path d="M3.5 9.5h17" /></svg>
              Custom range →
            </div>
          </div>
          <div style={{ padding: '16px 18px', flex: 1 }}>
            {/* Reset even when the popover is reopened during its exit animation. */}
            <CompactCalendar
              key={calendarSession}
              value={value}
              max={cap}
              onApply={(r) => {
                onChange(r);
                setOpen(false);
              }}
            />
          </div>
      </PopoverContent>
    </Popover>
  );
}
